import React, { useState, useEffect, useImperativeHandle, useCallback } from 'react';
import { connect } from 'react-redux';
import { InputSearch } from '../../../../components';
import { isNullOrEmptyArray } from '../../../../util/arrays';
import { removerCaracteresEspeciais } from '../../../../util/formatters/stringNormalizer';
import { adaptQuestionsToData } from './adapters';
import QuestionCollapsibleList from './QuestionCollapsibleListArray';

import * as Ui from './styles';

function MappedQuestionsAwareCollapsibleList({
  /**
   * identificador do cliente selecionado atualmente
   */
  customerId,
  /**
   * identificadores das pesquisas selecionadas
   */
  surveysIds = [],
  /**
   * Controle externo de estado, sao as perguntas que
   * devem ser marcadas automaticamente no seletor
   */
  selectedSurveysQuestions = [/* {surveyId, surveyTitle, questionId, questionTitle} */],
  /**
   * define o label usado para este componente como um todo
   */
  label,
  /**
   * conteúdo html ou component para ser renderizado como erro
   */
  errorComponent,
  /**
   * placeholder do input de pesquisa do selector
   */
  inputPlaceholder = 'Busque pelo enunciado',
  /**
   * propaga o evento de alteração na listagem de questões selecionadas
   */
  onSelectQuestions,
  /**
   * Perguntas a serem ignoradas na listagem [1,2,3...]
   */
  questionsToIgnore = [/* {surveyId, questionId} */],
  /**
   * Define se o item todos selecionados sera exibido
   */
  withAllSelected,
  /**
   * Define se o item allSelect esta ativo
   */
  allItemsSelected,
  /**
   * Outras propriedades implícitas
   */
  ...props
}, ref) {
  // timeout for load text in input
  let timeoutId = 0;

  // from redux
  const { surveys } = props;

  // states
  const [internalSelectedQuests, setInternalSelectedQuests] = useState(selectedSurveysQuestions);
  const [datasObj, setDatasObj] = useState([]);
  const [searchText, setSearchText] = useState('');

  // components wrapper
  function ErrorComponentWrapped() {
    return errorComponent ? (<>{errorComponent}</>) : null;
  }

  // callbacks: initialize datas
  function initialize(allSurveys, ignoredQIds, filterText) {
    // filter by text
    function filter(data, text) {
      if (!text) return data;
      const lowerCaseText = text.toLowerCase();
      const filterResult = data.map((item) => {
        if (!removerCaracteresEspeciais(item.label).toLowerCase().includes(lowerCaseText)) {
          return { ...item, hidden: true };
        }
        return item;
      });
      return filterResult;
    }
    // helper for build questions data
    function prepareNewQuestions() {
      // filter tmp
      const filteredSurveys = allSurveys.filter((s) => (
        surveysIds.includes(s.id)
      ));
      // adapt questions for this component
      const newQData = adaptQuestionsToData(
        [...filteredSurveys], internalSelectedQuests,
      );
      // remote quesions by questionToIgnore
      let newQDataWithIgnoredQIds = [];
      ignoredQIds.forEach((ignoredQId) => {
        newQDataWithIgnoredQIds = newQData.filter((q) => {
          const { surveyId, questionId } = q.value;
          const ignoredSurveyId = ignoredQId.surveyId;
          const ignoredQuestionId = ignoredQId.questionId;
          return !((surveyId === ignoredSurveyId) && (questionId === ignoredQuestionId));
        });
      });
      // set question data
      const sel = filter(newQDataWithIgnoredQIds, filterText);
      setDatasObj(sel);
    }
    // prepare customs questions
    if (!isNullOrEmptyArray(ignoredQIds) || !isNullOrEmptyArray(allSurveys)) {
      prepareNewQuestions();
    }
  }

  // effect of update questions
  useEffect(() => {
    initialize(surveys, questionsToIgnore, searchText);
  }, [surveys, questionsToIgnore, searchText]);

  // api para este componente poder
  // ser acesso pelo parent
  // on reset datas
  useImperativeHandle(ref, () => ({
    reset: () => {
      // reset search text
      setSearchText('');
      // initialize
      initialize(surveys, questionsToIgnore, '');
    },
  }));

  // handle change search text
  function handleChangeTextSearch(value) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      setSearchText(value);
    }, 300);
  }

  // handle on select item
  const handleSelectQuestionsCallback = useCallback((sel, allSel) => {
    const formattedData = sel ? sel.map((item) => (!item.questionTitle ? ({
      questionId: Number(item.value.split('-')[1]),
      questionTitle: item.label,
      surveyId: Number(item.value.split('-')[0]),
      surveyTitle: item.group,
      value: item.value,
      checked: true,
    }) : item)) : [];
    setInternalSelectedQuests(formattedData);
    if (onSelectQuestions) onSelectQuestions(formattedData, allSel);
  }, [onSelectQuestions]);

  const handleAllSelectedCallback = useCallback((sel, allSelected) => {
    const formattedData = sel ? sel.map((item) => ({
      questionId: Number(item.value.split('-')[1]),
      questionTitle: item.label,
      surveyId: Number(item.value.split('-')[0]),
      surveyTitle: item.group,
      value: item.value,
      checked: true,
    })) : [];
    setInternalSelectedQuests(formattedData);
    if (onSelectQuestions) onSelectQuestions(formattedData, allSelected);
  }, [onSelectQuestions]);

  return (
    <Ui.Container>
      <Ui.HeadSection>
        {/* filtro */}
        <Ui.FilterSectionItem>
          <Ui.Label>{label}</Ui.Label>
          <InputSearch
            placeholder={inputPlaceholder}
            styleClass="inputsearch"
            onChangeText={(value) => handleChangeTextSearch(value)}
          />
        </Ui.FilterSectionItem>
      </Ui.HeadSection>
      {/* error */}
      <Ui.ErrorSection>
        <ErrorComponentWrapped />
      </Ui.ErrorSection>
      {/* list */}
      <QuestionCollapsibleList
        datas={datasObj}
        onSelect={handleSelectQuestionsCallback}
        onSelectAll={handleAllSelectedCallback}
        listType={'singleQuestion'}
        withAllSelected={withAllSelected}
        setActiveItems
        internalSelectedIds={internalSelectedQuests}
        customerId={customerId}
        allItemsSelected={allItemsSelected}
        withArrow={false}
      />
    </Ui.Container>
  );
}

/**
 * mapeia a store para as propriedades que serão passadas para este
 * componente.
 *
 * Duas propriedades são extraídas e passadas para o componente:
 * questions => obtido da pesquisa selecionada
 *
 * @param {*} state
 * @param {*} props
 */
const mapStateToProps = (state) => {
  // states
  const surveysState = state.surveys;
  const surveysStatePayload = surveysState.payload;
  // result
  return {
    surveys: surveysStatePayload,
  };
};

// create component (delegate) and export
export default connect(
  mapStateToProps, null, null, { forwardRef: true },
)(
  React.forwardRef(MappedQuestionsAwareCollapsibleList),
);
